import { dataTransferWrapWithIncludedApiModule } from './dataTransferService'

import { CACHE_STORAGE_OPTIONS, STORE_MAP } from '@/config/cache/cache-engine'

export default class DataTransferModule {
	/**
	 * Создает экземпляр DataTransferModule.
	 *
	 * @param {string} moduleName - Имя модуля.
	 * @param {Object} apiModule - API-module с методами.
	 * @param {Object} params - Параметры модуля.
	 * @param {boolean} [params.useCache=false] - Использовать ли кеш.
	 * @param {boolean} [params.cacheStorageOption=CACHE_STORAGE_OPTIONS.WITH_SPLITTING] - Какой вариант хранения данных использовать. По умолчанию используется вариант с разделением данных на три уровня
	 * @param {boolean} [params.useExtraSingleLoadingMethod=true] - Использовать ли дополнительные методы загрузки сущности.
	 * @param {boolean} [params.hasParentEntity=false] - Указывает, есть ли родительская сущность.
	 * @param {Object} apiNames - Названия методов API. Следует указать если названия методов отличаются от стандартных getEntities или patchEntity и т.д.
	 * @param {string} apiNames.getEntities - Метод API для получения всех сущностей.
	 * @param {string} apiNames.createEntity - Метод API для создания сущности.
	 * @param {string} apiNames.getEntity - Метод API для получения одной сущности.
	 * @param {string} apiNames.patchEntity - Метод API для частичного обновления сущности.
	 * @param {string} apiNames.putEntity - Метод API для полного обновления сущности.
	 * @param {string} apiNames.deleteEntity - Метод API для удаления сущности.
	 * @param {string} [apiNames.getEntityPopup] - Метод API для получения сущности в попапе (если включено).
	 * @param {string} [apiNames.getEntityPreview] - Метод API для получения предварительного просмотра сущности (если включено).
	 * @param {string} [apiNames.getBasedEntities] - Метод API для получения базовых сущностей (если включено).
	 * @param {string} [apiNames.getExtendedEntities] - Метод API для получения расширенных сущностей (если включено).
	 */
	constructor(moduleName, apiModule, params, apiNames = {}) {
		// TODO: использовать этот метод
		this.dataTransferWrapMethod = dataTransferWrapWithIncludedApiModule

		this.moduleName = moduleName
		this.apiModule = apiModule

		/**
		 * @description имена url-параметров родителей для сущностей с несколькими родителями.
		 * Выводятся из apiModule.params.parentEntities, чтобы имена не рассинхронились с URL.
		 * null для сущностей без родителей и с одним родителем — тогда используется parentId.
		 */
		this.parentIdKeys = apiModule?.params?.parentEntities?.map(({ paramName }) => paramName) ?? null

		this.params = Object.assign(
			{
				useCache: false,
				cacheStorageOption: CACHE_STORAGE_OPTIONS.WITH_SPLITTING,
				dataDistributionSchema: null,
				useExtraSingleLoadingMethod: true,
				hasParentEntity: false,
			},
			params,
		)

		const _apiNames = Object.assign(
			{
				getEntities: 'getEntities',
				createEntity: 'createEntity',
				getEntity: 'getEntity',
				getEntityPopup: 'getEntityPopup',
				getEntityPreview: 'getEntityPreview',
				patchEntity: 'patchEntity',
				putEntity: 'putEntity',
				deleteEntity: 'deleteEntity',
			},
			apiNames,
		)
		this.apiMethodNames = {
			getEntities: _apiNames.getEntities,
			createEntity: _apiNames.createEntity,
			getEntity: _apiNames.getEntity,
			patchEntity: _apiNames.patchEntity,
			putEntity: _apiNames.putEntity,
			deleteEntity: _apiNames.deleteEntity,
			getEntityPopup: null,
			getEntityPreview: null,
			getBasedEntities: null,
			getExtendedEntities: null,
		}

		if (this.params.useExtraSingleLoadingMethod) {
			this.apiMethodNames.getEntityPopup = _apiNames.getEntityPopup
			this.apiMethodNames.getEntityPreview = _apiNames.getEntityPreview
		}

		if (this.params.useCache) {
			this.apiMethodNames.getBasedEntities = _apiNames.getBasedEntities
			this.apiMethodNames.getExtendedEntities = _apiNames.getExtendedEntities
		}
	}

	/**
	 * Кладёт id родителя(ей) в urlParams.
	 * Для сущностей с одним родителем parentId — скаляр, кладётся в urlParams.parentId (как и раньше).
	 * Для сущностей с несколькими родителями parentId — массив id в порядке parentEntities.
	 *
	 * @param {object} urlParams - объект url-параметров, мутируется
	 * @param {number|string|Array} parentId - id родителя или массив id родителей
	 */
	_assignParentIdParams(urlParams, parentId) {
		if (!this.parentIdKeys) {
			urlParams.parentId = parentId

			return
		}

		this._assertParentIdsMatchKeys(parentId)

		this.parentIdKeys.forEach((key, index) => {
			urlParams[key] = parentId[index]
		})
	}

	/**
	 * Связь массива id с именами url-параметров позиционная: порядок обязан совпадать
	 * с parentEntities в api-модуле. Без проверки недостающий id подставляется как undefined,
	 * литерал :paramName остаётся в пути, и запрос уходит на бэкенд — ошибка становится
	 * видна только там, по чужому ответу. Падение здесь громче тихого промаха.
	 *
	 * @param {*} parentId - то, что пришло вместо массива id родителей
	 */
	_assertParentIdsMatchKeys(parentId) {
		const keys = this.parentIdKeys.join(', ')

		if (!Array.isArray(parentId) || parentId.length !== this.parentIdKeys.length) {
			throw new Error(
				`${
					this.moduleName
				}: ожидался массив id родителей в порядке [${keys}], пришло ${JSON.stringify(parentId)}.`,
			)
		}

		const emptyIndex = parentId.findIndex((id) => id === undefined || id === null)

		if (emptyIndex !== -1) {
			const key = this.parentIdKeys[emptyIndex]

			throw new Error(
				`${this.moduleName}: id родителя ${key} не передан — в url остался бы литерал :${key}.`,
			)
		}
	}

	/**
	 * Режим WITHOUT_CHANGES кладёт сущность целиком в simple_storage, поэтому сторы срезов
	 * у неё пусты. Стор выбирает метод, а не режим: getBasedEntities и getExtendedEntities
	 * продолжили бы читать level_base и level_extended, промахивались бы мимо кэша на каждом
	 * вызове и молча ходили в сеть. Падение здесь громче тихого промаха.
	 *
	 * @param {string} storeName - Стор запрошенного среза.
	 */
	_assertSliceAvailable(storeName) {
		if (!this.params.useCache) return

		if (this.params.cacheStorageOption === CACHE_STORAGE_OPTIONS.WITHOUT_CHANGES) {
			throw new Error(
				`${this.moduleName}: срез ${storeName} недоступен при cacheStorageOption=${CACHE_STORAGE_OPTIONS.WITHOUT_CHANGES} — ` +
					`сущность хранится целиком в ${STORE_MAP.SIMPLE}. Используй getEntities или переключи модуль на ${CACHE_STORAGE_OPTIONS.WITH_SPLITTING}.`,
			)
		}
	}

	getEntities(...args) {
		let companyId = null
		let parentId = null
		let query = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, query] = args
		} else {
			;[companyId, query] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntities,
		}
		if (this.params.useCache) {
			config.useCache = true

			if (this.params.cacheStorageOption === CACHE_STORAGE_OPTIONS.WITH_SPLITTING) {
				config.cacheStorage = STORE_MAP.FULL
			} else if (this.params.cacheStorageOption === CACHE_STORAGE_OPTIONS.WITHOUT_CHANGES) {
				config.cacheStorage = STORE_MAP.SIMPLE
			}

			if (this.params.dataDistributionSchema) {
				config.dataDistributionSchema = this.params.dataDistributionSchema
			}
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
			},
		}

		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		if (query && typeof query === 'object') {
			apiMethodPayload.params = query
		}

		// debugger
		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	getExtendedEntities(...args) {
		this._assertSliceAvailable(STORE_MAP.EXTENDED)

		let companyId = null
		let parentId = null
		let query = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, query] = args
		} else {
			;[companyId, query] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntities,
		}
		if (this.params.useCache) {
			config.useCache = true
			config.cacheStorage = STORE_MAP.EXTENDED

			if (this.params.dataDistributionSchema) {
				config.dataDistributionSchema = this.params.dataDistributionSchema
			}
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
			},
		}

		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		if (query && typeof query === 'object') {
			apiMethodPayload.params = query
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	getBasedEntities(...args) {
		this._assertSliceAvailable(STORE_MAP.BASE)

		let companyId = null
		let parentId = null
		let query = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, query] = args
		} else {
			;[companyId, query] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntities,
		}
		if (this.params.useCache) {
			config.useCache = true
			config.cacheStorage = STORE_MAP.BASE

			if (this.params.dataDistributionSchema) {
				config.dataDistributionSchema = this.params.dataDistributionSchema
			}
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
			},
		}

		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		if (query && typeof query === 'object') {
			apiMethodPayload.params = query
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	createEntity(...args) {
		let companyId = null
		let parentId = null
		let body = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, body] = args
		} else {
			;[companyId, body] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.createEntity,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
			},
			...body,
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	getEntity(...args) {
		let companyId = null
		let parentId = null
		let entityId = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId] = args
		} else {
			;[companyId, entityId] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntity,
		}
		if (this.params.useCache) {
			config.useCache = true
			config.isSingleCachedEntity = true
			config.cacheStorage = STORE_MAP.FULL
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	getEntityPopup(...args) {
		let companyId = null
		let parentId = null
		let entityId = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId] = args
		} else {
			;[companyId, entityId] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntityPopup,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	getEntityPreview(...args) {
		let companyId = null
		let parentId = null
		let entityId = null
		let query = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId, query] = args
		} else {
			;[companyId, entityId, query] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.getEntityPreview,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		/**
		 * @description query-параметры одиночной сущности (например, период time_from/time_to у отчётов)
		 */
		if (query && typeof query === 'object') {
			apiMethodPayload.params = query
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	patchEntity(...args) {
		let companyId = null
		let parentId = null
		let entityId = null
		let body = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId, body] = args
		} else {
			;[companyId, entityId, body] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.patchEntity,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
			...body,
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	putEntity(...args) {
		let companyId = null
		let parentId = null
		let entityId = null
		let body = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId, body] = args
		} else {
			;[companyId, entityId, body] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.putEntity,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
			...body,
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}

	deleteEntity(...args) {
		let companyId = null
		let parentId = null
		let entityId = null

		if (this.params.hasParentEntity) {
			;[companyId, parentId, entityId] = args
		} else {
			;[companyId, entityId] = args
		}

		const config = {
			apiServiceMethodName: this.apiMethodNames.deleteEntity,
		}

		const apiMethodPayload = {
			urlParams: {
				companyId,
				entityId,
			},
		}
		if (this.params.hasParentEntity) {
			this._assignParentIdParams(apiMethodPayload.urlParams, parentId)
		}

		return dataTransferWrapWithIncludedApiModule(
			this.moduleName,
			this.apiModule,
			config,
			apiMethodPayload,
		)
	}
}
